import { fullPageReloadWithDelay } from '@/utils/reload.ts';
import {
  getSessionStorage,
  SESSION_STORAGE_KEY,
  setSessionStorage,
} from '@/app/components/search/utils/sessionStorage';

const CHUNK_RELOAD_MIN_INTERVAL_MS = 60_000;

// Turbopack throws a plain Error with this message when a module from the previous deploy is no longer served
const TURBOPACK_MISSING_MODULE = 'module factory is not available';

// What browsers reject a failed fetch or body read with: no stack, so only the exact wording identifies them.
const NETWORK_FAILURE =
  /^(?:Load failed|Failed to fetch|NetworkError when attempting to fetch resource\.|(?:NotReadableError: )?The I\/O read operation failed\.)$/;

export const isNetworkFailure = (message: string) => NETWORK_FAILURE.test(message);

// V8 starts error.stack with the non-empty parts of "name: message"; Safari and Firefox stacks are frames only.
export const splitStack = (error: Error) => {
  const heading = [error.name, error.message].filter(Boolean).join(': ');
  const stack = String(error.stack ?? '');
  const frames = stack.startsWith(heading) ? stack.slice(heading.length).replace(/^\n/, '') : stack;
  return { heading, frames };
};

export const isChunkLoadError = (error: Error) =>
  error?.name === 'ChunkLoadError' ||
  (typeof error?.message === 'string' && error.message.includes(TURBOPACK_MISSING_MODULE));

// At most one reload per tab per interval, so a chunk that is really missing never turns into a reload loop
const isChunkReloadAllowed = () => {
  const now = Date.now();
  const lastReloadAt = Number(getSessionStorage(SESSION_STORAGE_KEY.CHUNK_LOAD_ERROR_RELOAD_AT) ?? 0);
  if (now - lastReloadAt < CHUNK_RELOAD_MIN_INTERVAL_MS) return false;

  setSessionStorage(SESSION_STORAGE_KEY.CHUNK_LOAD_ERROR_RELOAD_AT, String(now));
  // Without storage the guard cannot hold across reloads, so skip the reload instead of risking a loop
  return getSessionStorage(SESSION_STORAGE_KEY.CHUNK_LOAD_ERROR_RELOAD_AT) === String(now);
};

// Module state dies with the reload, so it only marks a reload this page has scheduled and not yet run
let isReloadScheduled = false;

export const handleChunkLoadError = (error: Error) => {
  if (!isChunkLoadError(error)) return false;
  if (isReloadScheduled) return true;
  if (!isChunkReloadAllowed()) return false;

  isReloadScheduled = true;
  fullPageReloadWithDelay(1000);
  return true;
};
